import type { TipoEvento } from '../api/tipi.ts';

/**
 * Ricorda l'ultimo tipo di richiesta scelto nel pannello.
 *
 * Il pannello si apre ogni volta da capo, e il tipo di partenza era sempre
 * "Ferie". Chi inserisce una serie di spostamenti doveva riselezionare
 * "Spostamento" a ogni giorno: e' bastata una dimenticanza, il 28 ottobre
 * 2026, per lasciare in mezzo a quattro spostamenti una Ferie finita anche
 * nel riepilogo delle modifiche.
 *
 * Sta nella sessione del browser (sessionStorage), non nell'account: dura
 * finche' la scheda e' aperta e non segue la persona su un altro computer. Chi
 * apre il sito la prima volta trova "Ferie", che e' la richiesta piu' comune.
 *
 * Il magazzino si passa dal chiamante perche' puo' mancare o lanciare
 * un'eccezione (navigazione privata, dati del sito bloccati): in quel caso
 * tutto funziona come prima, senza memoria.
 */

const CHIAVE = 'gestionale-assenze.ultimo-tipo-richiesta';

export type Magazzino = Pick<Storage, 'getItem' | 'setItem'>;

function magazzinoDellaSessione(): Magazzino | undefined {
  try {
    return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
  } catch {
    return undefined;
  }
}

/**
 * L'ultimo tipo scelto, se e' ancora fra quelli offerti a questa persona.
 *
 * Il controllo sui tipi ammessi serve perche' il valore salvato potrebbe non
 * valere piu': per esempio il riposo produzione, nascosto a chi non puo'
 * chiederlo, o un nome che un domani cambia.
 */
export function ultimoTipoScelto(
  ammessi: readonly TipoEvento[],
  magazzino: Magazzino | undefined = magazzinoDellaSessione(),
): TipoEvento | null {
  try {
    const salvato = magazzino?.getItem(CHIAVE) ?? null;
    return ammessi.find((tipo) => tipo === salvato) ?? null;
  } catch {
    return null;
  }
}

export function ricordaTipoScelto(
  tipo: TipoEvento,
  magazzino: Magazzino | undefined = magazzinoDellaSessione(),
): void {
  try {
    magazzino?.setItem(CHIAVE, tipo);
  } catch {
    // Senza memoria si riparte da "Ferie": non vale un errore a schermo.
  }
}
